import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Loader2, AlertCircle, Key, Table, X, FileText, Package } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AVAILABLE_KEY_FIELDS = [
  { id: 'material_id', label: 'Material', description: 'Material Number', defaultLevel: 'ITEM' },
  { id: 'customer_id', label: 'Customer', description: 'Customer ID', defaultLevel: 'HEADER' },
  { id: 'material_group', label: 'Material Group', description: 'Material Group', defaultLevel: 'ITEM' },
  { id: 'sales_org', label: 'Sales Org', description: 'Sales Organization', defaultLevel: 'HEADER' },
  { id: 'distribution_channel', label: 'Dist. Channel', description: 'Distribution Channel', defaultLevel: 'HEADER' },
  { id: 'division', label: 'Division', description: 'Division', defaultLevel: 'HEADER' },
  { id: 'plant', label: 'Plant', description: 'Plant', defaultLevel: 'ITEM' },
  { id: 'customer_group', label: 'Cust. Group', description: 'Customer Group', defaultLevel: 'HEADER' },
  { id: 'price_list', label: 'Price List', description: 'Price List Type', defaultLevel: 'HEADER' },
];

const ConditionTables = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('pricing');
  const [viewItem, setViewItem] = useState(null);
  const [conditionTables, setConditionTables] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [showAddCustomField, setShowAddCustomField] = useState(false);
  const [customFieldData, setCustomFieldData] = useState({ 
    field_id: '', 
    field_label: '', 
    field_description: '',
    field_type: 'KEY_FIELD',  // KEY_FIELD or ASSIGNMENT
    level: 'ITEM'  // HEADER or ITEM
  });
  const [formData, setFormData] = useState({
    condition_table_id: '',
    description: '',
    key_field_definitions: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pricing/condition-tables`);
      setConditionTables(response.data || []);
    } catch (error) {
      console.error('Error loading condition tables:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load condition tables: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (table = null) => {
    if (table) {
      setEditingTable(table);
      // Convert legacy key_fields to key_field_definitions if needed
      let definitions = table.key_field_definitions || [];
      if (definitions.length === 0 && table.key_fields?.length > 0) {
        definitions = table.key_fields.map(fieldId => {
          const stdField = AVAILABLE_KEY_FIELDS.find(f => f.id === fieldId);
          return {
            field_id: fieldId,
            field_label: stdField?.label || fieldId,
            field_description: stdField?.description || '',
            level: stdField?.defaultLevel || 'ITEM',
            is_custom: !stdField
          };
        });
      }
      setFormData({
        condition_table_id: table.condition_table_id,
        description: table.description,
        key_field_definitions: definitions
      });
    } else {
      setEditingTable(null);
      setFormData({
        condition_table_id: '',
        description: '',
        key_field_definitions: []
      });
    }
    setShowAddCustomField(false);
    setCustomFieldData({ 
      field_id: '', 
      field_label: '', 
      field_description: '',
      field_type: 'KEY_FIELD',
      level: 'ITEM'
    });
    setIsDialogOpen(true);
  };

  const toggleKeyField = (fieldId) => {
    const exists = formData.key_field_definitions.find(f => f.field_id === fieldId);
    if (exists) {
      setFormData({
        ...formData,
        key_field_definitions: formData.key_field_definitions.filter(f => f.field_id !== fieldId)
      });
    } else {
      const stdField = AVAILABLE_KEY_FIELDS.find(f => f.id === fieldId);
      setFormData({
        ...formData,
        key_field_definitions: [...formData.key_field_definitions, {
          field_id: fieldId,
          field_label: stdField?.label || fieldId,
          field_description: stdField?.description || '',
          level: stdField?.defaultLevel || 'ITEM',
          is_custom: false
        }]
      });
    }
  };

  const updateFieldLevel = (fieldId, level) => {
    setFormData({
      ...formData,
      key_field_definitions: formData.key_field_definitions.map(f =>
        f.field_id === fieldId ? { ...f, level } : f
      )
    });
  };

  const removeField = (fieldId) => {
    setFormData({
      ...formData,
      key_field_definitions: formData.key_field_definitions.filter(f => f.field_id !== fieldId)
    });
  };

  const addCustomField = () => {
    if (!customFieldData.field_id || !customFieldData.field_label) {
      toast.error('Field ID and Label are required');
      return;
    }
    if (formData.key_field_definitions.find(f => f.field_id === customFieldData.field_id)) {
      toast.error('Field ID already exists');
      return;
    }
    setFormData({
      ...formData,
      key_field_definitions: [...formData.key_field_definitions, {
        field_id: customFieldData.field_id.toLowerCase().replace(/\s+/g, '_'),
        field_label: customFieldData.field_label,
        field_description: customFieldData.field_description || '',
        level: customFieldData.level,
        field_type: customFieldData.field_type,
        is_custom: true
      }]
    });
    setCustomFieldData({ 
      field_id: '', 
      field_label: '', 
      field_description: '',
      field_type: 'KEY_FIELD',
      level: 'ITEM'
    });
    setShowAddCustomField(false);
    toast.success(`Custom ${customFieldData.field_type === 'KEY_FIELD' ? 'key field' : 'assignment field'} added at ${customFieldData.level} level`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.condition_table_id || formData.condition_table_id.trim().length === 0) {
      toast.error('Condition Table ID is required');
      return;
    }
    if (formData.condition_table_id.length > 10) {
      toast.error('Condition Table ID must be max 10 characters');
      return;
    }
    if (!formData.description || formData.description.trim().length === 0) {
      toast.error('Description is required');
      return;
    }
    if (formData.key_field_definitions.length === 0) {
      toast.error('At least one key field is required');
      return;
    }

    setIsSaving(true);
    try {
      const data = {
        condition_table_id: formData.condition_table_id.toUpperCase().trim(),
        description: formData.description.trim(),
        key_fields: formData.key_field_definitions.map(f => f.field_id), // Legacy support
        key_field_definitions: formData.key_field_definitions
      };

      if (editingTable) {
        await axios.put(`${BACKEND_URL}/api/pricing/condition-tables/${editingTable.condition_table_id}`, data);
        toast.success(`Condition Table '${editingTable.condition_table_id}' updated successfully`);
      } else {
        await axios.post(`${BACKEND_URL}/api/pricing/condition-tables`, data);
        toast.success(`Condition Table '${data.condition_table_id}' created successfully`);
      }
      setIsDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving condition table:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to save condition table: ${errorMsg}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (tableId) => {
    if (!window.confirm(`Are you sure you want to delete Condition Table '${tableId}'? This action cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${BACKEND_URL}/api/pricing/condition-tables/${tableId}`);
      toast.success(`Condition Table '${tableId}' deleted successfully`);
      loadData();
    } catch (error) {
      console.error('Error deleting condition table:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to delete condition table: ${errorMsg}`);
    }
  };

  const getFieldLabel = (fieldId) => {
    const field = AVAILABLE_KEY_FIELDS.find(f => f.id === fieldId);
    return field?.label || fieldId;
  };

  const getFieldDefinitionDisplay = (table) => {
    const defs = table.key_field_definitions || [];
    if (defs.length === 0 && table.key_fields?.length > 0) {
      return table.key_fields.map(f => ({ field_id: f, level: 'ITEM', field_label: getFieldLabel(f) }));
    }
    return defs;
  };

  const headerFields = formData.key_field_definitions.filter(f => f.level === 'HEADER');
  const itemFields = formData.key_field_definitions.filter(f => f.level === 'ITEM');

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Condition Tables</h1>
            <p className="text-sm text-gray-600 mt-1">Define key field combinations for condition records</p>
          </div>
          {writable && (<Button onClick={() => handleOpenDialog()} data-testid="add-condition-table-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Condition Table
          </Button>)}
        </div>

        <Card className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
              <span className="ml-3 text-gray-600">Loading condition tables...</span>
            </div>
          ) : conditionTables.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No condition tables found.</p>
              <p className="text-sm text-gray-400 mt-1">Click "Add Condition Table" to create one.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="condition-tables-table">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Table ID</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Description</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Key Fields</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {conditionTables.map((table) => (
                    <tr key={table.condition_table_id} className="border-b hover:bg-gray-50" data-testid={`table-${table.condition_table_id}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Table className="w-4 h-4 text-gray-400" />
                          <span className="font-mono font-bold">{table.condition_table_id}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">{table.description}</td>
                      <td className="py-3 px-4">
                        <div className="space-y-1">
                          {getFieldDefinitionDisplay(table).filter(f => f.level === 'HEADER').length > 0 && (
                            <div className="flex flex-wrap gap-1 items-center">
                              <span className="text-xs text-orange-600 font-medium mr-1">Header:</span>
                              {getFieldDefinitionDisplay(table).filter(f => f.level === 'HEADER').map((field, index) => (
                                <span key={index} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  <FileText className="w-3 h-3" />
                                  {field.field_label || getFieldLabel(field.field_id)}
                                </span>
                              ))}
                            </div>
                          )}
                          {getFieldDefinitionDisplay(table).filter(f => f.level === 'ITEM' || !f.level).length > 0 && (
                            <div className="flex flex-wrap gap-1 items-center">
                              <span className="text-xs text-green-600 font-medium mr-1">Item:</span>
                              {getFieldDefinitionDisplay(table).filter(f => f.level === 'ITEM' || !f.level).map((field, index) => (
                                <span key={index} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <Package className="w-3 h-3" />
                                  {field.field_label || getFieldLabel(field.field_id)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-1">
                          <RecordActions doctype="pricing" onView={() => setViewItem(table)} onEdit={() => handleOpenDialog(table)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTable ? 'Edit Condition Table' : 'Create Condition Table'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="condition_table_id">Condition Table ID * (max 10 chars)</Label>
                  <Input
                    id="condition_table_id"
                    value={formData.condition_table_id}
                    onChange={(e) => setFormData({...formData, condition_table_id: e.target.value.toUpperCase()})}
                    placeholder="e.g., T001, T002"
                    maxLength={10}
                    disabled={!!editingTable}
                    className="uppercase"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="e.g., Material Price, Customer-Material Price"
                    maxLength={100}
                    required
                  />
                </div>
              </div>

              {/* Standard Key Fields Selection */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700">Select Key Fields *</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Select fields and assign them to Header (document level) or Item (line level)
                    </p>
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowAddCustomField(!showAddCustomField)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Custom Field
                  </Button>
                </div>

                {/* Custom Field Form */}
                {showAddCustomField && (
                  <div className="mb-4 p-4 bg-white rounded-lg border-2 border-dashed border-orange-300">
                    <h4 className="text-sm font-medium text-orange-700 mb-3">Add Custom Field</h4>
                    
                    {/* Field Type & Level Selection */}
                    <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-orange-50 rounded-lg">
                      <div>
                        <Label className="text-xs font-semibold text-gray-700 mb-2 block">Field Type *</Label>
                        <div className="space-y-2">
                          <label className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                            customFieldData.field_type === 'KEY_FIELD' 
                              ? 'bg-orange-100 border-orange-400' 
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}>
                            <input
                              type="radio"
                              name="field_type"
                              value="KEY_FIELD"
                              checked={customFieldData.field_type === 'KEY_FIELD'}
                              onChange={(e) => setCustomFieldData({...customFieldData, field_type: e.target.value})}
                              className="text-orange-600"
                            />
                            <div>
                              <p className="font-medium text-sm">Key Field</p>
                              <p className="text-xs text-gray-500">Used to identify condition records (e.g., Customer, Material)</p>
                            </div>
                          </label>
                          <label className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                            customFieldData.field_type === 'ASSIGNMENT' 
                              ? 'bg-orange-100 border-orange-400' 
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}>
                            <input
                              type="radio"
                              name="field_type"
                              value="ASSIGNMENT"
                              checked={customFieldData.field_type === 'ASSIGNMENT'}
                              onChange={(e) => setCustomFieldData({...customFieldData, field_type: e.target.value})}
                              className="text-orange-600"
                            />
                            <div>
                              <p className="font-medium text-sm">Assignment Field</p>
                              <p className="text-xs text-gray-500">Determines which condition types apply (e.g., Pricing Procedure)</p>
                            </div>
                          </label>
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-xs font-semibold text-gray-700 mb-2 block">Field Level *</Label>
                        <div className="space-y-2">
                          <label className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                            customFieldData.level === 'HEADER' 
                              ? 'bg-orange-100 border-orange-400' 
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}>
                            <input
                              type="radio"
                              name="field_level"
                              value="HEADER"
                              checked={customFieldData.level === 'HEADER'}
                              onChange={(e) => setCustomFieldData({...customFieldData, level: e.target.value})}
                              className="text-orange-600"
                            />
                            <div>
                              <p className="font-medium text-sm">Header Level</p>
                              <p className="text-xs text-gray-500">Applies to entire document (e.g., Customer, Sales Org)</p>
                            </div>
                          </label>
                          <label className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                            customFieldData.level === 'ITEM' 
                              ? 'bg-green-100 border-green-400' 
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}>
                            <input
                              type="radio"
                              name="field_level"
                              value="ITEM"
                              checked={customFieldData.level === 'ITEM'}
                              onChange={(e) => setCustomFieldData({...customFieldData, level: e.target.value})}
                              className="text-green-600"
                            />
                            <div>
                              <p className="font-medium text-sm">Item Level</p>
                              <p className="text-xs text-gray-500">Applies to line items (e.g., Material, Plant)</p>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    {/* Field Details */}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Field ID *</Label>
                        <Input
                          value={customFieldData.field_id}
                          onChange={(e) => setCustomFieldData({...customFieldData, field_id: e.target.value})}
                          placeholder="e.g., region_code"
                          className="text-sm"
                        />
                        <p className="text-xs text-gray-400 mt-1">Unique identifier (no spaces)</p>
                      </div>
                      <div>
                        <Label className="text-xs">Display Label *</Label>
                        <Input
                          value={customFieldData.field_label}
                          onChange={(e) => setCustomFieldData({...customFieldData, field_label: e.target.value})}
                          placeholder="e.g., Region"
                          className="text-sm"
                        />
                        <p className="text-xs text-gray-400 mt-1">User-friendly name</p>
                      </div>
                      <div>
                        <Label className="text-xs">Description</Label>
                        <Input
                          value={customFieldData.field_description}
                          onChange={(e) => setCustomFieldData({...customFieldData, field_description: e.target.value})}
                          placeholder="e.g., Sales Region Code"
                          className="text-sm"
                        />
                        <p className="text-xs text-gray-400 mt-1">Optional details</p>
                      </div>
                    </div>
                    
                    {/* Preview */}
                    {customFieldData.field_id && customFieldData.field_label && (
                      <div className="mt-3 p-2 bg-gray-50 rounded border">
                        <p className="text-xs font-medium text-gray-600">Preview:</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            customFieldData.field_type === 'KEY_FIELD' 
                              ? 'bg-orange-100 text-orange-800' 
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {customFieldData.field_type === 'KEY_FIELD' ? 'Key Field' : 'Assignment'}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            customFieldData.level === 'HEADER' 
                              ? 'bg-orange-100 text-orange-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {customFieldData.level === 'HEADER' ? 'Header' : 'Item'}
                          </span>
                          <span className="font-medium text-sm">{customFieldData.field_label}</span>
                          <span className="text-xs text-gray-500">({customFieldData.field_id})</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex gap-2 mt-3">
                      <Button type="button" size="sm" onClick={addCustomField}>
                        Add Custom Field
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => setShowAddCustomField(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {/* Standard Fields Grid */}
                <div className="grid grid-cols-3 gap-2">
                  {AVAILABLE_KEY_FIELDS.map((field) => {
                    const isSelected = formData.key_field_definitions.find(f => f.field_id === field.id);
                    return (
                      <label
                        key={field.id}
                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-orange-50 border-orange-300'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={!!isSelected}
                          onChange={() => toggleKeyField(field.id)}
                          className="rounded border-gray-300"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{field.label}</p>
                          <p className="text-xs text-gray-500 truncate">{field.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Selected Fields with Level Assignment */}
              {formData.key_field_definitions.length > 0 && (
                <div className="bg-white border rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Selected Key Fields - Assign Levels</h3>
                  
                  {/* Header Fields */}
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-orange-600" />
                      <span className="text-sm font-medium text-orange-700">Header Level Fields</span>
                      <span className="text-xs text-gray-500">(Document level - e.g., Customer, Sales Org)</span>
                    </div>
                    <div className="flex flex-wrap gap-2 min-h-[40px] p-2 bg-orange-50 rounded border border-orange-200">
                      {headerFields.length === 0 ? (
                        <span className="text-xs text-orange-400 italic">No header fields selected</span>
                      ) : (
                        headerFields.map((field) => (
                          <div key={field.field_id} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-orange-100 text-orange-800 text-sm">
                            {field.field_type === 'ASSIGNMENT' && (
                              <span className="text-xs px-1 bg-orange-200 text-orange-700 rounded">A</span>
                            )}
                            <span>{field.field_label}</span>
                            {field.is_custom && <span className="text-xs text-orange-500">(custom)</span>}
                            <button
                              type="button"
                              onClick={() => updateFieldLevel(field.field_id, 'ITEM')}
                              className="ml-1 text-orange-600 hover:text-orange-800"
                              title="Move to Item level"
                            >
                              ↓
                            </button>
                            <button
                              type="button"
                              onClick={() => removeField(field.field_id)}
                              className="text-orange-600 hover:text-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Item Fields */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">Item Level Fields</span>
                      <span className="text-xs text-gray-500">(Line item level - e.g., Material, Plant)</span>
                    </div>
                    <div className="flex flex-wrap gap-2 min-h-[40px] p-2 bg-green-50 rounded border border-green-200">
                      {itemFields.length === 0 ? (
                        <span className="text-xs text-green-400 italic">No item fields selected</span>
                      ) : (
                        itemFields.map((field) => (
                          <div key={field.field_id} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-green-100 text-green-800 text-sm">
                            {field.field_type === 'ASSIGNMENT' && (
                              <span className="text-xs px-1 bg-orange-200 text-orange-700 rounded">A</span>
                            )}
                            <span>{field.field_label}</span>
                            {field.is_custom && <span className="text-xs text-green-500">(custom)</span>}
                            <button
                              type="button"
                              onClick={() => updateFieldLevel(field.field_id, 'HEADER')}
                              className="ml-1 text-green-600 hover:text-green-800"
                              title="Move to Header level"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              onClick={() => removeField(field.field_id)}
                              className="text-green-600 hover:text-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
                    <strong>Key Combination:</strong>{' '}
                    {formData.key_field_definitions.map(f => f.field_label).join(' + ')}
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-end pt-4 border-t mt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isSaving}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving} data-testid="submit-condition-table">
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {editingTable ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingTable ? 'Update Condition Table' : 'Create Condition Table'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default ConditionTables;
